import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from '@mui/material/Divider';
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import FilterTags from '../../Components/LineDetails/CommentDetails/FilterTags';
import CommentSearch from '../../Components/LineDetails/CommentDetails/CommentSearch';
import CommentTags from '../../Components/LineDetails/CommentDetails/CommentTags';

function GlobalListOptions(props) {
    const { getGlobalLists, globalLists, isLoading, hasMore, currentPage, filterTagList,
        selectedFilterTagID, setSelectedFilterTagID, searchData, setSearchData, setState,
        setPage, addDropdown, id, categoryId, isInitialMountRef } = props;
    const storeData = useSelector((state) => state.CommonReducer);

    const loadingRef = useRef(null);
    const observerRef = useRef(null);
    const contentRef = useRef(null);
    const stickyHeaderRef = useRef(null);

    const handleLoadMore = useCallback(async () => {
        if (!isLoading && hasMore && getGlobalLists) {
            await getGlobalLists(currentPage + 1, false, searchData, selectedFilterTagID);
        }
    }, [isLoading, hasMore, getGlobalLists, currentPage, searchData, selectedFilterTagID]);

    useEffect(() => {
        if (!hasMore || isLoading) return;
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting) handleLoadMore();
            },
            { root: null, rootMargin: "50px", threshold: 0.1 }
        );
        const currentLoadingRef = loadingRef.current;
        if (currentLoadingRef && hasMore) observerRef.current.observe(currentLoadingRef);
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [handleLoadMore, hasMore, isLoading]);

    useEffect(() => {
        if (stickyHeaderRef.current && contentRef.current) {
            const headerHeight = stickyHeaderRef.current.offsetHeight;
            contentRef.current.style.paddingTop = `${headerHeight}px`;
        }
    }, []);

    const handleSearchFilter = async (value) => {
        setPage(1);
        setSearchData(value);
    };

    const handleSelectItem = async (event, item) => {
        event.stopPropagation();
        await addDropdown(event, item);
        setState(prevState => ({ ...prevState, open: false }));
        setSelectedFilterTagID([]);
        setPage(0);
        isInitialMountRef.current = true;
    }

    return (
        <Grid container style={{ position: 'relative' }}>
            <Grid
                ref={stickyHeaderRef}
                item
                xs={12}
                sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '16px 0' }}
            >
                <Grid container spacing={2} sx={{ padding: '0 16px' }}>
                    <Grid item xs={5.95}>
                        <FilterTags filterTagList={filterTagList} selectedFilterTagID={selectedFilterTagID || []} setSelectedFilterTagID={setSelectedFilterTagID}
                            getClListForSearch={getGlobalLists} setPage={setPage} searchInput={searchData}
                            component={'global_lists'} categoryId={categoryId}
                            defaultCategories={storeData.defaultCategories}
                        />
                    </Grid>
                    <Grid item xs={5.95}>
                        <div className="cl-search" style={{ position: 'relative' }}>
                            <CommentSearch onSearch={(val) => handleSearchFilter(val)} value={searchData} />
                            <SearchIcon className="cl-search-icon" />
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <Grid ref={contentRef} item container xs={12} sx={{ overflowY: 'auto', height: 'calc(100% - 70px)', paddingTop: "10px !important" }}>
                {globalLists?.length > 0 ? globalLists?.map((list, index) => (
                    <Box sx={{ cursor: 'pointer', width: "100%" }} key={`list-${index}`} onClick={(e) => handleSelectItem(e, list)}>
                        <Grid item display={"flex"} xs={12} mt={1.7} mb={0.7} width={"100%"}>
                            {list?.name && !(/^dropdown\s+[a-z0-9-]+$/i.test(list?.name)) ? <Grid item display={"flex"} xs={2} ml={2} mt={0}>
                                <Typography className={list?.name ? "dropdown_name" : 'untitled_text'}>
                                    {/^dropdown\s+[a-z0-9-]+$/i.test(list?.name) ? "" : list?.name || '[untitled]'}
                                </Typography>
                            </Grid> : null}
                            <Grid item container display={"flex"} xs={list?.name ? 9.6 : 12} mx={2} mb={1} >
                                <Grid id={id} item xs={12} display={"flex"} mt={0} flexWrap="wrap" alignItems="center" >
                                    {Array.isArray(list?.options) && list?.options?.map((item, i) => (
                                        <React.Fragment key={`item-${i}`}>
                                            <Typography className='options_text'>{item}</Typography>
                                            {i < list?.options?.length - 1 && (
                                                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: "#d1d1d1ff !important" }} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Grid item xs={12}>
                                    <CommentTags tags={list?.tags} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider sx={{ width: "100%", borderColor: "#color/neutral/neutral-300 !important" }} />
                    </Box >
                )) : !isLoading && (
                    <Grid container justifyContent="center" sx={{ padding: 4 }}>
                        <Typography>No global lists found</Typography>
                    </Grid>
                )}
                {hasMore && (
                    <div
                        ref={loadingRef}
                        style={{ height: "20px", width: "100%", margin: "20px 0" }}
                    />
                )}
                {isLoading && (
                    <Grid container justifyContent="center" sx={{ padding: 2 }}>
                        <Typography>Loading more lists...</Typography>
                    </Grid>
                )}
                {!hasMore && globalLists?.length > 0 && (
                    <Grid container display={"flex"} justifyContent="center" alignItems={"flex-end"} sx={{ padding: 1 }} >
                        <Typography>All lists loaded</Typography>
                    </Grid>
                )}
            </Grid>
        </Grid >
    );
}

export default GlobalListOptions;